import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell } from "lucide-react";
import api from "@/lib/api";

export const NotificationBell = () => {
  const [data, setData] = useState({ items: [], unread: 0 });
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const navigate = useNavigate();

  const load = () => api.get("/notifications").then((r) => setData(r.data)).catch(() => {});

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => { clearInterval(t); document.removeEventListener("mousedown", onClick); };
  }, []);

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next && data.unread > 0) {
      await api.post("/notifications/read").catch(() => {});
      setData((d) => ({ ...d, items: d.items.map((i) => ({ ...i, read: true })), unread: 0 }));
    }
  };

  const go = (link) => { setOpen(false); navigate(link); };

  const timeAgo = (iso) => {
    if (!iso) return "";
    const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
    if (s < 60) return "just now";
    if (s < 3600) return `${Math.floor(s / 60)}m ago`;
    if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
    return `${Math.floor(s / 86400)}d ago`;
  };

  return (
    <div className="relative" ref={ref}>
      <button onClick={toggle} data-testid="notification-bell" aria-label="Notifications"
        className="relative w-10 h-10 rounded-full flex items-center justify-center text-[#0B1733] hover:bg-[#E8F5F1] transition-colors">
        <Bell size={20} />
        {data.unread > 0 && (
          <span data-testid="notif-unread-badge" className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-[#007F83] text-white text-[10px] font-bold flex items-center justify-center">
            {data.unread > 9 ? "9+" : data.unread}
          </span>
        )}
      </button>
      {open && (
        <div className="absolute right-0 mt-2 w-80 max-h-[420px] overflow-y-auto bg-white rounded-2xl border border-[#E3E8E8] shadow-soft z-50" data-testid="notif-dropdown">
          <div className="px-4 py-3 border-b border-[#E3E8E8]"><p className="font-display font-bold text-[#0B1733]">Notifications</p></div>
          {data.items.length === 0 ? (
            <p className="px-4 py-8 text-center text-sm text-[#4A5568]">No notifications yet</p>
          ) : (
            data.items.map((n) => (
              <button key={n.id} onClick={() => go(n.link)} data-testid={`notif-${n.id}`}
                className={`w-full text-left px-4 py-3 border-b border-[#E3E8E8] last:border-0 hover:bg-[#E8F5F1] transition-colors ${!n.read ? "bg-[#F3FAF8]" : ""}`}>
                <div className="flex items-start justify-between gap-2">
                  <p className="font-semibold text-sm text-[#0B1733]">{n.title}</p>
                  <span className="text-[11px] text-[#8a97a3] shrink-0">{timeAgo(n.created_at)}</span>
                </div>
                <p className="text-xs text-[#4A5568] mt-0.5">{n.text}</p>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
};
